import { useState } from "react";
import {
  ArrowUp,
  ArrowDown,
  Trash,
  NotePencil,
  Lock,
  LockOpen,
} from "@phosphor-icons/react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  getCardClass,
  getStatusBadgeClass,
  getTextPrimary,
  getTextMuted,
  getHoverRowClass,
} from "../../lib/styleHelpers";

function formatCurrency(val) {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(1)}K`;
  return `$${val.toFixed(2)}`;
}

function PortfolioItemNotes({ item, editingNotes, editNotes, setEditNotes, saveNotes, startEditNotes, setEditingNotes, inputClass, isDark }) {
  if (editingNotes) {
    return (
      <div className="flex gap-2 mt-1">
        <input
          type="text"
          value={editNotes}
          onChange={(e) => setEditNotes(e.target.value)}
          placeholder="Investment notes..."
          className={`flex-1 text-sm px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-1 focus:ring-purple-500 ${inputClass}`}
          data-testid={`portfolio-notes-input-${item.id}`}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") saveNotes();
            if (e.key === "Escape") setEditingNotes(false);
          }}
        />
        <Button
          size="sm"
          onClick={saveNotes}
          className="bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-lg"
          data-testid={`portfolio-save-notes-${item.id}`}
        >
          Save
        </Button>
      </div>
    );
  }

  if (item.notes) {
    return (
      <button
        onClick={startEditNotes}
        className={`text-xs font-body mt-1 px-3 py-1.5 rounded-lg border cursor-pointer text-left block w-full ${isDark ? "bg-[#0a0a14] border-white/10 text-gray-400 hover:border-purple-500/30" : "bg-gray-50 border-gray-200 text-gray-600 hover:border-purple-300"}`}
        data-testid={`portfolio-notes-${item.id}`}
      >
        <NotePencil size={12} className="inline mr-1 opacity-50" />
        {item.notes}
      </button>
    );
  }

  return (
    <button
      onClick={startEditNotes}
      className={`text-xs font-body mt-1 hover:text-purple-500 transition-colors ${getTextMuted(isDark)}`}
      data-testid={`portfolio-add-note-${item.id}`}
    >
      <NotePencil size={12} className="inline mr-1" />
      Add notes...
    </button>
  );
}

export function PortfolioItemRow({ item, onUpdateItem, onRemoveItem, onViewOpportunity, isDark }) {
  const [editingValue, setEditingValue] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [editNotes, setEditNotes] = useState("");

  const startEditValue = () => {
    setEditingValue(true);
    setEditValue(String(item.current_value));
  };

  const saveValue = () => {
    const val = parseFloat(editValue);
    if (!isNaN(val) && val >= 0) {
      onUpdateItem(item.id, { current_value: val });
    }
    setEditingValue(false);
  };

  const startEditNotes = () => {
    setEditingNotes(true);
    setEditNotes(item.notes || "");
  };

  const saveNotes = () => {
    onUpdateItem(item.id, { notes: editNotes });
    setEditingNotes(false);
  };

  const categoryBadgeClass = isDark
    ? "border-purple-500/30 text-purple-300"
    : "border-purple-300 text-purple-600";

  const inputClass = isDark
    ? "bg-[#0a0a14] border-white/10 text-white placeholder:text-gray-600"
    : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400";

  return (
    <div className={`p-4 transition-colors ${getHoverRowClass(isDark)}`} data-testid={`portfolio-item-${item.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Title row */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge variant="outline" className={`text-xs ${categoryBadgeClass}`}>
              {item.opportunity_category?.split(" ")[0]}
            </Badge>
            <Badge className={`text-xs ${getStatusBadgeClass(item.status, isDark)}`}>
              {item.status === "open" ? <LockOpen size={12} className="mr-1" /> : <Lock size={12} className="mr-1" />}
              {item.status === "open" ? "Open" : "Closed"}
            </Badge>
          </div>

          <button
            onClick={() => onViewOpportunity(item.opportunity_id)}
            className={`text-left font-bold mb-2 hover:text-purple-500 transition-colors block truncate max-w-full ${getTextPrimary(isDark)}`}
            data-testid={`portfolio-view-${item.id}`}
          >
            {item.opportunity_title}
          </button>

          {/* Metrics row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
            <div>
              <span className={`text-[10px] ${getTextMuted(isDark)}`}>Entry Price</span>
              <p className={`text-sm font-bold ${getTextPrimary(isDark)}`}>${item.entry_price.toFixed(2)}</p>
            </div>
            <div>
              <span className={`text-[10px] ${getTextMuted(isDark)}`}>Invested</span>
              <p className={`text-sm font-bold ${getTextPrimary(isDark)}`}>{formatCurrency(item.amount_invested)}</p>
            </div>
            <div>
              <span className={`text-[10px] ${getTextMuted(isDark)}`}>Current Value</span>
              {editingValue ? (
                <div className="flex gap-1">
                  <input
                    type="number"
                    step="0.01"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className={`w-24 text-sm px-2 py-0.5 rounded border focus:outline-none focus:ring-1 focus:ring-purple-500 ${inputClass}`}
                    data-testid={`portfolio-value-input-${item.id}`}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveValue();
                      if (e.key === "Escape") setEditingValue(false);
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={saveValue}
                    className="bg-purple-600 hover:bg-purple-700 text-white text-[10px] h-6 px-2 rounded"
                    data-testid={`portfolio-save-value-${item.id}`}
                  >
                    OK
                  </Button>
                </div>
              ) : (
                <button
                  onClick={() => item.status === "open" && startEditValue()}
                  className={`text-sm font-bold block ${item.status === "open" ? "cursor-pointer hover:text-purple-500" : "cursor-default"} ${getTextPrimary(isDark)}`}
                  data-testid={`portfolio-current-${item.id}`}
                  disabled={item.status !== "open"}
                >
                  {formatCurrency(item.current_value)}
                  {item.status === "open" && <NotePencil size={10} className="inline ml-1 opacity-40" />}
                </button>
              )}
            </div>
            <div>
              <span className={`text-[10px] ${getTextMuted(isDark)}`}>P&L</span>
              <p className={`text-sm font-bold flex items-center gap-1 ${item.pnl >= 0 ? "text-emerald-500" : "text-red-500"}`} data-testid={`portfolio-pnl-${item.id}`}>
                {item.pnl >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                {formatCurrency(Math.abs(item.pnl))} ({item.pnl_percent >= 0 ? "+" : ""}{item.pnl_percent}%)
              </p>
            </div>
          </div>

          {/* Notes */}
          <PortfolioItemNotes
            item={item}
            editingNotes={editingNotes}
            editNotes={editNotes}
            setEditNotes={setEditNotes}
            saveNotes={saveNotes}
            startEditNotes={startEditNotes}
            setEditingNotes={setEditingNotes}
            inputClass={inputClass}
            isDark={isDark}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {item.status === "open" && (
            <Button
              size="sm"
              onClick={() => onUpdateItem(item.id, { status: "closed" })}
              className={`text-xs rounded-lg ${isDark ? "bg-white/5 hover:bg-white/10 text-gray-300" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
              data-testid={`portfolio-close-${item.id}`}
            >
              <Lock size={14} className="mr-1" />
              Close Position
            </Button>
          )}
          <button
            onClick={() => onRemoveItem(item.id)}
            className={`p-1.5 rounded-lg transition-colors ${isDark ? "hover:bg-red-500/10 text-gray-500 hover:text-red-400" : "hover:bg-red-50 text-gray-400 hover:text-red-500"}`}
            title="Remove investment"
            data-testid={`portfolio-remove-${item.id}`}
          >
            <Trash size={16} />
          </button>
          <span className={`text-[10px] ${getTextMuted(isDark)}`}>
            {new Date(item.entry_date).toLocaleDateString()}
          </span>
        </div>
      </div>
    </div>
  );
}
